import { collection, doc, getDocs, addDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { DaySchedule } from '../types/schedule';

export interface Campus {
    id: string;
    name: string; // Branch Name
    address: string; // Address
    schoolStartTime?: string;
    schoolEndTime?: string;
    days?: DaySchedule[];
    isActive: boolean;
    createdAt: Date;
    updatedAt: Date;
}

export interface Class {
    id: string;
    grade: string;
    section: string;
    campusId: string;
    subjects: string[];
    createdAt: Date;
    updatedAt: Date;
}

export interface Badge {
    id: string;
    campusId: string;
    badgeNumber: number; // Auto increased per campus starting from 1
    title: string;
    year: number;
    createdAt: Date;
    updatedAt: Date;
}

// Campus CRUD Operations
export const getCampuses = async (): Promise<Campus[]> => {
    try {
        const campusesRef = collection(db, 'campuses');
        const querySnapshot = await getDocs(campusesRef);
        const campuses = querySnapshot.docs.map(doc => {
            const data = doc.data();
            return {
                id: doc.id,
                ...data,
                createdAt: data.createdAt?.toDate() || new Date(),
                updatedAt: data.updatedAt?.toDate() || new Date(),
            };
        }) as Campus[];

        campuses.sort((a, b) => a.name.localeCompare(b.name));
        return campuses;
    } catch (error) {
        console.error('Error fetching campuses:', error);
        return [];
    }
};

export const addCampus = async (campusData: { name: string; address: string }): Promise<string> => {
    try {
        const campusesRef = collection(db, 'campuses');
        const docRef = await addDoc(campusesRef, {
            name: campusData.name,
            address: campusData.address,
            schoolStartTime: '',
            schoolEndTime: '',
            days: [],
            isActive: true,
            createdAt: new Date(),
            updatedAt: new Date(),
        });
        return docRef.id;
    } catch (error) {
        console.error('Error adding campus:', error);
        throw error;
    }
};

export const updateCampus = async (id: string, campusData: Partial<Campus>): Promise<void> => {
    try {
        const campusRef = doc(db, 'campuses', id);
        await updateDoc(campusRef, {
            ...campusData,
            updatedAt: new Date(),
        });
    } catch (error) {
        console.error('Error updating campus:', error);
        throw error;
    }
};

export const deleteCampus = async (id: string): Promise<void> => {
    try {
        const campusRef = doc(db, 'campuses', id);
        await deleteDoc(campusRef);
    } catch (error) {
        console.error('Error deleting campus:', error);
        throw error;
    }
};

// Classes CRUD Operations (Campus integrated)
export const getClassesWithCampus = async (): Promise<Class[]> => {
    try {
        const classesRef = collection(db, 'classes');
        const querySnapshot = await getDocs(classesRef);
        const classes = querySnapshot.docs.map(doc => {
            const data = doc.data();
            return {
                id: doc.id,
                ...data,
                createdAt: data.createdAt?.toDate() || new Date(),
                updatedAt: data.updatedAt?.toDate() || new Date(),
            };
        }) as Class[];

        classes.sort((a, b) => {
            if (a.grade !== b.grade) {
                return a.grade.localeCompare(b.grade);
            }
            return a.section.localeCompare(b.section);
        });
        return classes;
    } catch (error) {
        console.error('Error fetching classes:', error);
        return [];
    }
};

export const addClassWithCampus = async (classData: { grade: string; section: string; campusId: string }): Promise<string> => {
    try {
        const classesRef = collection(db, 'classes');
        const docRef = await addDoc(classesRef, {
            grade: classData.grade,
            section: classData.section,
            campusId: classData.campusId,
            subjects: [],
            createdAt: new Date(),
            updatedAt: new Date(),
        });
        return docRef.id;
    } catch (error) {
        console.error('Error adding class:', error);
        throw error;
    }
};

export const updateClassWithCampus = async (id: string, classData: Partial<Class>): Promise<void> => {
    try {
        const classRef = doc(db, 'classes', id);
        await updateDoc(classRef, {
            ...classData,
            updatedAt: new Date(),
        });
    } catch (error) {
        console.error('Error updating class:', error);
        throw error;
    }
};

export const deleteClassWithCampus = async (id: string): Promise<void> => {
    try {
        const classRef = doc(db, 'classes', id);
        await deleteDoc(classRef);
    } catch (error) {
        console.error('Error deleting class:', error);
        throw error;
    }
};

// Badges CRUD Operations
export const getBadges = async (): Promise<Badge[]> => {
    try {
        const badgesRef = collection(db, 'badges');
        const querySnapshot = await getDocs(badgesRef);
        const badges = querySnapshot.docs.map(doc => {
            const data = doc.data();
            return {
                id: doc.id,
                ...data,
                createdAt: data.createdAt?.toDate() || new Date(),
                updatedAt: data.updatedAt?.toDate() || new Date(),
            };
        }) as Badge[];

        badges.sort((a, b) => b.year - a.year || a.badgeNumber - b.badgeNumber);
        return badges;
    } catch (error) {
        console.error('Error fetching badges:', error);
        return [];
    }
};

export const addBadge = async (badgeData: { campusId: string; badgeNumber: number; title: string; year: number }): Promise<string> => {
    try {
        const badgesRef = collection(db, 'badges');
        const docRef = await addDoc(badgesRef, {
            ...badgeData,
            createdAt: new Date(),
            updatedAt: new Date(),
        });
        return docRef.id;
    } catch (error) {
        console.error('Error adding badge:', error);
        throw error;
    }
};

export const deleteBadge = async (id: string): Promise<void> => {
    try {
        const badgeRef = doc(db, 'badges', id);
        await deleteDoc(badgeRef);
    } catch (error) {
        console.error('Error deleting badge:', error);
        throw error;
    }
};

export const updateBadge = async (id: string, badgeData: Partial<Badge>): Promise<void> => {
    try {
        const badgeRef = doc(db, 'badges', id);
        await updateDoc(badgeRef, {
            ...badgeData,
            updatedAt: new Date(),
        });
    } catch (error) {
        console.error('Error updating badge:', error);
        throw error;
    }
};

